/**
 * Cursor-based prev/next pagination.
 *
 * The list APIs are forward-only keyset-paginated (each page returns a
 * `nextCursor`; there is no "jump to page N" or total count). `useCursorPager`
 * keeps a stack of the cursors visited so far so the user can step backwards as
 * well as forwards; `<CursorPaginationBar>` renders the controls.
 */

import {
  cn,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@cometchat-ai/ui-dashboard";
import { useCallback, useState } from "react";

export interface CursorPager {
  /** Cursor for the page currently being viewed (undefined = first page). */
  cursor: string | undefined;
  /** 0-based index of the current page. */
  pageIndex: number;
  /** True when there is a previous page to step back to. */
  canPrev: boolean;
  /** Advance to the next page using the current page's `nextCursor`. No-op if null. */
  toNext: (nextCursor: string | null | undefined) => void;
  /** Step back one page. */
  toPrev: () => void;
  /** Return to the first page — call when the query's filters/search change. */
  reset: () => void;
}

export function useCursorPager(): CursorPager {
  // stack[i] is the cursor that fetches page i; stack[0] is always undefined.
  const [stack, setStack] = useState<Array<string | undefined>>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);

  const toNext = useCallback(
    (nextCursor: string | null | undefined) => {
      if (!nextCursor) return;
      setStack((prev) => (pageIndex + 1 >= prev.length ? [...prev, nextCursor] : prev));
      setPageIndex((i) => i + 1);
    },
    [pageIndex],
  );

  const toPrev = useCallback(() => setPageIndex((i) => Math.max(0, i - 1)), []);

  const reset = useCallback(() => {
    setStack([undefined]);
    setPageIndex(0);
  }, []);

  return { cursor: stack[pageIndex], pageIndex, canPrev: pageIndex > 0, toNext, toPrev, reset };
}

export function CursorPaginationBar({
  pageIndex,
  canPrev,
  canNext,
  onPrev,
  onNext,
  busy = false,
  "data-testid": testId,
}: {
  pageIndex: number;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  busy?: boolean;
  "data-testid"?: string;
}) {
  // Hide entirely when there is only a single page.
  if (pageIndex === 0 && !canNext) return null;

  const prevDisabled = !canPrev || busy;
  const nextDisabled = !canNext || busy;

  return (
    <Pagination className="items-center justify-between" data-testid={testId}>
      <span className="text-2sm text-muted-foreground">Page {pageIndex + 1}</span>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            role="button"
            aria-disabled={prevDisabled}
            onClick={(e) => {
              e.preventDefault();
              if (!prevDisabled) onPrev();
            }}
            className={cn("rounded-lg", prevDisabled && "pointer-events-none opacity-50")}
          />
        </PaginationItem>
        <PaginationItem>
          <PaginationNext
            href="#"
            role="button"
            aria-disabled={nextDisabled}
            onClick={(e) => {
              e.preventDefault();
              if (!nextDisabled) onNext();
            }}
            className={cn("rounded-lg", nextDisabled && "pointer-events-none opacity-50")}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
